import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Form } from 'antd';
import { CheckCircleOutlined, CheckOutlined } from '@ant-design/icons';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;

const NewClusterModal = ({ 
    visible, 
    onClose, 
    onCreateCluster, 
    onUpdateCluster, 
    loading = false, 
    isRTL = false, 
    isEdit = false, 
    editData = null,
    dmode 
}) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [clusterName, setClusterName] = useState('');
    const [notes, setNotes] = useState('');

    useEffect(() => {
        if (visible && isEdit && editData) {
            form.setFieldsValue({
                clusterName: editData.clusterName,
                notes: editData.description || editData.clusterDescription
            });
            setClusterName(editData.clusterName);
            setNotes(editData.description || editData.clusterDescription || '');
        } else if (visible && !isEdit) {
            form.resetFields();
            setClusterName('');
            setNotes('');
        }
    }, [visible, isEdit, editData, form]);

    const handleSubmit = () => {
        form.validateFields().then((values) => {
            if (isEdit) {
                onUpdateCluster({
                    clusterId: editData.clusterId || editData.id,
                    clusterName: values.clusterName,
                    clusterDescription: values.notes
                });
            } else {
                onCreateCluster({
                    clusterName: values.clusterName,
                    clusterDescription: values.notes
                });
            }
            form.resetFields();
            setClusterName('');
            setNotes('');
            onClose();
        }).catch((errorInfo) => {
            console.log('Validation failed:', errorInfo);
        });
    };

    const handleCancel = () => {
        form.resetFields();
        setClusterName('');
        setNotes('');
        onClose();
    };

    return (
        <Modal
            title={
                <span style={{ color: dmode ? '#ccc' : '#333' }}>
                    {isEdit ? t('EDIT_CLUSTER') : t('NEW_CLUSTER')}
                </span>
            }
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={400}
            centered
            closeIcon={
            <span className='close-btn-pp-add'>
                <XMarkIcon
              style={{
                width: 20,
                height: 20,
              }} />
            </span>
            }
            styles={{
                header: {
                    fontFamily: 'Noto Sans, sans-serif',
                    borderBottom: 'none',
                    paddingTop: '24px',
                    paddingLeft: '16px',
                    direction: isRTL ? 'rtl' : 'ltr',
                    backgroundColor: dmode ? '#2f353e' : 'white',
                },
                body: {
                    fontFamily: 'Mulish, sans-serif',
                    padding: '8px 0 0 0',
                    direction: isRTL ? 'rtl' : 'ltr',
                    backgroundColor: dmode ? '#2f353e' : 'white'
                },
                content: {
                    fontFamily: 'Mulish, sans-serif',
                    borderRadius: '26px',
                    padding: '20px',
                    backgroundColor: dmode ? '#2f353e' : 'white'
                }
            }}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                style={{ 
                    marginTop: '4px',
                    direction: isRTL ? 'rtl' : 'ltr',
                }}
            >
                <>
                    <style>
                        {`.ant-form-item .ant-form-item-label > label.ant-form-item-required::before {
                            margin-inline-end: 1px !important;
                            color: red !important;
                        }
                        .close-btn {
                            border: 1px solid #828282 !important;
                            font-weight: 700 !important;
                        }
                        .close-btn:hover {
                            color: rgba(0, 0, 0, 0.6) !important;
                            border-color: #828282 !important;
                        }
                    
                        .ant-input, .ant-input:focus {
                            border-color: #4F4F4F !important;
                        }
                        .ant-input::placeholder {
                            color: #4F4F4F !important;
                        }
                        .ant-form-item-explain-error {
                            font-family: Mulish, sans-serif !important;
                            font-weight: 500 !important;
                            margin-top: 4px !important;
                            font-size: 12px !important;
                        }
                        .ant-modal-close:hover {
                            opacity: 0.7 !important;
                            border-radius: 100% !important;
                            background-color: transparent !important;
                        }`}
                    </style>
                    <Form.Item
                        label={
                            <span style={{ 
                                fontSize: '14px', 
                                fontWeight: '700', 
                                color: dmode ? '#ccc' : '#333',
                                fontFamily: 'Mulish, sans-serif',
                            }}>
                                <span style={{ color: 'red', fontFamily: 'Assistant, sans-serif', marginRight: 1 }}>*</span>{t('CLUSTER_NAME')}
                            </span>
                        }
                        name="clusterName"
                        rules={[
                            { validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error(t('CLUSTER_NAME_REQUIRED'))) },
                        ]}
                        required={false}
                        style={{ 
                            marginBottom: '20px', 
                            padding: '0 16px'
                        }}
                        className="cluster-name-form-item"
                >
                    <Input
                        placeholder={t('CLUSTER_NAME_PLACEHOLDER')}
                        value={clusterName}
                        onChange={(e) => setClusterName(e.target.value)}
                        style={{
                            borderRadius: '4px',
                            border: '1px solid #828282',
                            padding: '8px 12px',
                            fontFamily: 'Mulish, sans-serif',
                            textAlign: isRTL ? 'right' : 'left'
                        }}
                    />
                </Form.Item>

                <Form.Item
                    label={
                        <span style={{ 
                            fontSize: '14px', 
                            fontWeight: '700', 
                            color: dmode ? '#ccc' : '#333',
                            fontFamily: 'Mulish, sans-serif'
                        }}>
                            {t('NOTES')}
                        </span>
                    }
                    name="notes"
                    style={{ marginBottom: '32px', padding: '0 16px' }}
                >
                    <TextArea
                        placeholder={t('NOTES_PLACEHOLDER')}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={4}
                        style={{
                            borderRadius: '4px',
                            border: '1px solid #828282',
                            padding: '8px 12px',
                            fontFamily: 'Mulish, sans-serif',
                            resize: 'none',
                            textAlign: isRTL ? 'right' : 'left'
                        }}
                    />
                </Form.Item>

                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginTop: '24px',
                    borderTop: '1px solid #4F4F4F',
                    marginBottom: '16px',
                    paddingTop: '16px',
                    flexDirection: isRTL ? 'row-reverse' : 'row'
                }}>
                    {isRTL ? (
                        <>
                            <Button
                                type="primary"
                                htmlType="submit"
                                style={{
                                    backgroundColor: '#219653',
                                    borderColor: '#219653',
                                    borderRadius: '4px',
                                    padding: '6px 16px',
                                    height: 'auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontFamily: 'Mulish, sans-serif',
                                    fontSize: '14px',
                                }}
                            >
                                {isEdit ? t('UPDATE_CLUSTER') : t('CREATE_CLUSTER')} {<CheckCircleOutlined style={{ fontSize: '16px' }} />}
                            </Button>
                            <Button
                                onClick={handleCancel}
                                className="close-btn"
                                style={{
                                    border: '1px solid #828282',
                                    borderRadius: '4px',
                                    padding: '6px 20px',
                                    height: 'auto',
                                    fontFamily: 'Mulish, sans-serif',
                                    fontSize: '14px',
                                    fontWeight: '700',
                                }}
                            >
                                {t('CLOSE')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button
                                onClick={handleCancel}
                                className="close-btn"
                                style={{
                                    border: '1px solid #d9d9d9',
                                    borderRadius: '4px',
                                    padding: '6px 20px',
                                    height: 'auto',
                                    fontFamily: 'Mulish, sans-serif',
                                    fontSize: '14px'
                                }}
                            >
                                {t('CLOSE')}
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                style={{
                                    backgroundColor: '#219653',
                                    borderColor: '#219653',
                                    borderRadius: '4px',
                                    padding: '6px 16px',
                                    height: 'auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontFamily: 'Mulish, sans-serif',
                                    fontSize: '14px'
                                }}
                            >
                                {isEdit ? t('UPDATE_CLUSTER') : t('CREATE_CLUSTER')} {<CheckCircleOutlined style={{ fontSize: '16px' }} />}
                            </Button>
                        </>
                    )}
                </div>
                </>
            </Form>
        </Modal>
    );
};

export default NewClusterModal;